import { parseEmsDate } from "../../Logic/emsDates";
import { ProcessingStatus } from "../EmailSearchAPI";

/**
 * The one dialect of "absent versus blank" this feature speaks.
 *
 * The worklist row an operator clicks and the detail page it opens are two
 * renderings of the same email, so they have to say "not measured", "present
 * but empty" and "no subject" in exactly the same words. When each component
 * owned its own copy nothing enforced that; the labels and titles below are
 * shared precisely so the two cannot drift apart.
 */

/**
 * The pill class per status.
 *
 * A `Record`, so a tenth `ProcessingStatus` cannot compile until someone
 * decides how it reads. A chain of `if`s would have rendered the new status
 * unstyled with every test still green.
 */
export const STATUS_PILL: Record<ProcessingStatus, string> = {
    Failed: "mp-pill mp-pill-fail",
    DeadLettered: "mp-pill mp-pill-dead",
    Abandoned: "mp-pill mp-pill-aband",
    Queued: "mp-pill mp-pill-flight",
    Processing: "mp-pill mp-pill-flight",
    Building: "mp-pill mp-pill-flight",
    Sendable: "mp-pill mp-pill-flight",
    Completed: "mp-pill mp-pill-done",
    Sent: "mp-pill mp-pill-done",
};

/** The absent marker, with a title explaining what "absent" means here. */
export function absent(title: string) {
    return (
        <span
            className="mp-absent"
            title={title}
        >
            —
        </span>
    );
}

/**
 * A field that IS present but happens to be empty — as opposed to `absent`,
 * which is "not measured". Sharing the `mp-absent` look (both are "nothing to
 * read here") but never the em dash: this feature reserves "—" for "not
 * measured", and reusing it here would give one row two different reasons to
 * show the same glyph.
 */
export function blank(label: string, title: string) {
    return (
        <span
            className="mp-absent"
            title={title}
        >
            {label}
        </span>
    );
}

/**
 * `from`/`to` come from a linked Message, same as `subject` (contract's
 * `subject` doc comment). `undefined` means no message is linked yet;
 * `""` means a message IS linked and the header was blank (e.g. a BCC-only
 * inbound message has no `To:` at all). `??` only catches the first case, so
 * an empty header used to render a silently blank cell — this makes both
 * states visible and tells them apart.
 */
export function optionalText(value: string | undefined, absentTitle: string, blankTitle: string) {
    if (value === undefined) {
        return absent(absentTitle);
    }
    if (value === "") {
        return blank("(none)", blankTitle);
    }
    return value;
}

/** A clock time, or the absent marker. Never a blank cell. */
export function clock(stamp?: string) {
    const parsed = parseEmsDate(stamp);
    if (!parsed) {
        return absent("Not recorded");
    }
    return <span className="mp-num">{parsed.toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>;
}

/**
 * The subject's three-way split, as a label and a title rather than as an
 * element.
 *
 * `undefined` means no message was ever linked, so there is no subject to
 * have; `""` means one linked and its subject line was genuinely blank. Both
 * are `blank`, not `absent`: the message row exists either way, so the em
 * dash — "not measured" — would be the wrong glyph.
 *
 * Returns the copy, not markup, because the two callers wrap it differently
 * and neither wrapping is the shared one: the worklist puts a `<span>` inside
 * the button that opens the row, while the detail page renders a
 * `<p className="mp-detail-subject …">` heading. Forcing one element on both
 * would have meant restyling one of them to share the strings, and it is the
 * strings — the label AND its title — that have to match between the row an
 * operator clicks and the page it opens. `undefined` back means the email has
 * a real subject; render it however that caller renders real text.
 */
export function subjectMarker(subject: string | undefined): { label: string; title: string } | undefined {
    if (subject === undefined) {
        return {
            label: "(no subject yet)",
            title: "This email failed before its message was linked, so it has no subject yet",
        };
    }
    if (subject === "") {
        return { label: "(no subject)", title: "This message linked successfully; its subject line is blank" };
    }
    return undefined;
}
